import { AmbienceBank } from "./ambience-bank.ts";
import type { AmbienceMix, BedId, SynthCover } from "./ambience-model.ts";
import { AmbiencePlayer, type AmbienceStats } from "./ambience-player.ts";
import { MUSIC_TRACKS } from "../assets/sounds.generated.ts";
import { audible, crossfade, DEFAULT_VOLUMES, PRIORITY, trackFor, VariationPicker, VoicePool, VOICES, type MusicState, type TrackId, type VoicePriority, type Volumes } from "./audio-model.ts";
import { MusicPlayer, type MusicStats } from "./music-player.ts";
import { retainNodes } from "./retain-nodes.ts";
import { playNote, type Samples } from "./instruments.ts";
import { scoreBar, tempo } from "./music-score.ts";
import { RECIPES, setRenderOffset, SOUND_IDS, type SoundId } from "./recipes.ts";
import { SampleBank, type SampleStats } from "./sample-bank.ts";

/** Taxa das amostras pré-renderizadas: mono a 22 050 Hz (memória baixa; o timbre aguenta). */
const SAMPLE_RATE = 22_050;
/** Sequenciador: agenda notas até 0,3 s à frente, a cada 50 ms. */
const LOOKAHEAD = 0.3;
const TICK_MS = 50;

export type Bus = "musica" | "efeitos" | "ambiente" | "ui";

export interface PlayOptions {
  /** Posição no mundo; sem ela, toca "na cabeça" (UI, o próprio corpo). */
  x?: number;
  y?: number;
  z?: number;
  priority?: VoicePriority;
  gain?: number;
  bus?: Bus;
  /** Velocidade fixa (sem a variação de ±8 %). */
  rate?: number;
  /** Atraso (s). */
  delay?: number;
  /** Força a amostra ou a síntese e a variação (a página `?debug=sons`); sem elas, a amostra quando houver, sorteada. */
  source?: SoundSource;
  variation?: number;
}

export type SoundSource = "amostra" | "sintese";

/** Pitch aleatório na hora de tocar: ±8 % na síntese, ±4 % nas amostras (gravadas soam falsas acima disso). */
const JITTER: Record<SoundSource, number> = { sintese: 0.08, amostra: 0.04 };

export interface LoopHandle {
  /** Falso quando ainda não há contexto ou buffer: quem pediu tenta de novo depois. */
  live: boolean;
  set(x: number | undefined, y: number, z: number, gain: number): void;
  stop(fade?: number): void;
}

export interface AudioStats {
  ready: number;
  state: string;
  unlocked: boolean;
  hidden: boolean;
  music: MusicState;
  lowpass: number;
  voices: number;
  loops: number;
  /** Pedidos (com ou sem contexto) e o que de fato começou a tocar. */
  played: Record<string, number>;
  started: Record<string, number>;
  /** Notas da trilha agendadas (partitura) e a faixa gravada (F84). */
  notes: number;
  track: MusicStats;
  /** Amostras (F45): carregamento e quantas começaram a tocar, por som. */
  samples: SampleStats & { started: Record<string, number> };
  /** Leitos de ambiente (F46): a mistura, o banco (LRU, decodificações, PCM) e as esperas. */
  ambience: AmbienceStats;
}

/**
 * Áudio do jogo (doc 05; ADR 0007 e 0009): `AudioContext` → passa-baixa (Vida e água; a UI passa
 * por fora) → limitador → saída, com buses de música, efeitos, ambiente e UI e um envio de reverb
 * (resposta ao impulso de ruído com decaimento). Efeitos pré-renderizados num `OfflineAudioContext`
 * (que não precisa de gesto) e, por cima, as amostras gravadas (F45, `SampleBank`) dos sons que as
 * têm; a síntese fica de reserva de todos. Tocados com pitch aleatório, variação sem repetir a
 * anterior e até 24 vozes com prioridade; HRTF para o que é perto e importante. Os leitos de
 * ambiente gravados (F46, `AmbiencePlayer`) vão por um ganho próprio (`leitos`) sob o volume de
 * ambiente, para o passa-baixa e sem o envio de reverb. O contexto nasce no primeiro gesto e fica
 * suspenso com a aba oculta. A trilha toca as faixas gravadas (F84, `MusicPlayer`) nos estados
 * que têm uma e a partitura procedural nos outros (ou quando a faixa falha). Nada aqui pode lançar
 * exceção: sem WebAudio, o jogo segue mudo.
 */
export class AudioEngine {
  private readonly buffers = new Map<SoundId, AudioBuffer[]>();
  private ctx: AudioContext | undefined;
  private lowpass: BiquadFilterNode | undefined;
  private reverbSend: GainNode | undefined;
  private readonly buses = new Map<Bus, GainNode>();
  private master: GainNode | undefined;
  private readonly pool = new VoicePool();
  private readonly sources = new Map<number, AudioBufferSourceNode>();
  private readonly loops = new Set<LoopHandle>();
  private volumes: Volumes = { ...DEFAULT_VOLUMES };
  private rendering: Promise<void> | undefined;
  /** Só o render da síntese (o portão das decodificações) e se ele está em andamento. */
  private rendered: Promise<void> | undefined;
  private renderingNow = false;
  private leitos: GainNode | undefined;
  private lowpassHz = 22_000;
  private listener = { x: 0, y: 0, z: 0 };
  private readonly played: Record<string, number> = {};
  private readonly started: Record<string, number> = {};
  private readonly samplesStarted: Record<string, number> = {};
  private readonly bank: SampleBank;
  readonly ambienceBank: AmbienceBank;
  private readonly ambiencePlayer: AmbiencePlayer;
  private readonly picker = new VariationPicker();
  private notes = 0;
  private hidden = typeof document !== "undefined" && document.hidden;
  // Sequenciador da trilha.
  private music: MusicState = "silencio";
  private musicGain: GainNode | undefined;
  private intensity = 0;
  private seed = 0;
  private bar = 0;
  private nextBar = 0;
  private timer: ReturnType<typeof setInterval> | undefined;
  private readonly player: MusicPlayer;

  constructor(bank: SampleBank = new SampleBank(), ambience?: AmbienceBank, music: MusicPlayer = new MusicPlayer(MUSIC_TRACKS)) {
    this.bank = bank;
    this.player = music;
    this.player.onFail = (id) => this.onTrackFail(id);
    this.player.setHidden(this.hidden);
    // Leitos: decodificam depois do render da síntese, no formato que o banco de efeitos escolheu.
    this.ambienceBank = ambience ?? new AmbienceBank({ gate: () => this.renderGate(), rendering: () => this.renderingNow, format: () => this.bank.stats().format });
    this.ambiencePlayer = new AmbiencePlayer(this.ambienceBank);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        this.hidden = document.hidden;
        this.player.setHidden(this.hidden);
        void (this.hidden ? this.ctx?.suspend() : this.ctx?.resume())?.catch(() => undefined);
      });
    }
  }

  /**
   * Pré-renderiza todos os efeitos e carrega as amostras (no carregamento; pode começar antes do
   * gesto). As amostras baixam junto, mas só decodificam depois do render (o WebKit cai
   * decodificando durante um render offline). Nunca rejeita.
   */
  prerender(onProgress?: (fraction: number) => void): Promise<void> {
    this.rendering ??= (async () => {
      this.renderingNow = true;
      const render = this.renderAll(onProgress)
        .catch(() => undefined)
        .finally(() => (this.renderingNow = false));
      this.rendered = render;
      await Promise.all([render, this.bank.load(render)]);
    })();
    return this.rendering;
  }

  /** O fim do render da síntese (começa o pré-render se ninguém começou). */
  private renderGate(): Promise<void> {
    if (!this.rendered) void this.prerender();
    return this.rendered ?? Promise.resolve();
  }

  /**
   * Em lotes de ~12 s de áudio por `OfflineAudioContext`, um depois do outro: cada variação no seu
   * trecho de tempo (com folga), renderizado e recortado em buffers. Um contexto por variação (144)
   * prende threads de áudio no Chromium; um contexto só, com milhares de nós, derruba o WebKit.
   */
  private async renderAll(onProgress?: (fraction: number) => void): Promise<void> {
    if (typeof OfflineAudioContext === "undefined") return;
    const GAP = 0.25;
    const BATCH_SECONDS = 12;
    const batches: { id: SoundId; v: number; start: number; seconds: number }[][] = [[]];
    let at = 0;
    for (const id of SOUND_IDS) {
      for (let v = 0; v < RECIPES[id].variations; v++) {
        const seconds = RECIPES[id].seconds;
        if (at > 0 && at + seconds > BATCH_SECONDS) {
          batches.push([]);
          at = 0;
        }
        batches[batches.length - 1]!.push({ id, v, start: at, seconds });
        at += seconds + GAP;
      }
    }
    let done = 0;
    for (const batch of batches) {
      const last = batch[batch.length - 1]!;
      const ctx = new OfflineAudioContext(1, Math.ceil((last.start + last.seconds + GAP) * SAMPLE_RATE), SAMPLE_RATE);
      const kept = retainNodes(ctx);
      try {
        for (const slot of batch) {
          // Variação reproduzível: a mesma receita dá os mesmos sons em todo cliente.
          let s = (slot.v + 1) * 2654435761 + slot.id.length * 97;
          const r = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 0xffffffff);
          setRenderOffset(slot.start);
          RECIPES[slot.id].build(ctx, ctx.destination, r);
        }
      } finally {
        setRenderOffset(0);
      }
      const all = (await ctx.startRendering()).getChannelData(0);
      kept.length = 0;
      for (const slot of batch) {
        const first = Math.round(slot.start * SAMPLE_RATE);
        const len = Math.max(1, Math.round(slot.seconds * SAMPLE_RATE));
        const b = ctx.createBuffer(1, len, SAMPLE_RATE);
        b.copyToChannel(all.subarray(first, first + len), 0);
        const list = this.buffers.get(slot.id) ?? [];
        list.push(b);
        this.buffers.set(slot.id, list);
      }
      onProgress?.(++done / batches.length);
    }
  }

  /** Primeiro gesto do jogador: cria ou retoma o contexto (política de autoplay). */
  unlock(): void {
    try {
      if (!this.ctx) this.build();
      if (!this.hidden) void this.ctx?.resume().catch(() => undefined);
      this.player.prime();
    } catch {
      this.ctx = undefined;
    }
  }

  private build(): void {
    const Ctor = typeof AudioContext !== "undefined" ? AudioContext : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -10;
    limiter.ratio.value = 12;
    const master = ctx.createGain();
    master.connect(limiter).connect(ctx.destination);
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = this.lowpassHz;
    lowpass.connect(master);
    // Reverb: resposta ao impulso de ruído com decaimento exponencial (2 s), noite e grutas.
    const convolver = ctx.createConvolver();
    const ir = ctx.createBuffer(2, ctx.sampleRate * 2, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = ir.getChannelData(c);
      for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp((-4 * i) / d.length);
    }
    convolver.buffer = ir;
    const send = ctx.createGain();
    send.gain.value = 0;
    send.connect(convolver).connect(lowpass);
    for (const bus of ["musica", "efeitos", "ambiente", "ui"] as const) {
      const g = ctx.createGain();
      if (bus === "ui") g.connect(master);
      else {
        g.connect(lowpass);
        if (bus !== "musica") g.connect(send);
      }
      this.buses.set(bus, g);
    }
    // Leitos de ambiente (F46): sob o volume de ambiente, para o passa-baixa, sem o envio de reverb
    // (a gravação já traz o espaço dela).
    const leitos = ctx.createGain();
    leitos.connect(lowpass);
    this.leitos = leitos;
    this.ambiencePlayer.attach(ctx, leitos);
    this.player.attach(ctx, this.buses.get("musica")!);
    this.ctx = ctx;
    this.master = master;
    this.lowpass = lowpass;
    this.reverbSend = send;
    this.applyVolumes();
    this.timer = setInterval(() => this.schedule(), TICK_MS);
    // A trilha pedida antes do primeiro gesto (o menu) entra agora.
    if (this.music !== "silencio") this.route(crossfade(this.music));
  }

  setVolumes(v: Volumes): void {
    this.volumes = { ...v };
    this.applyVolumes();
  }

  private applyVolumes(): void {
    if (!this.ctx) return;
    this.master?.gain.setTargetAtTime(this.volumes.geral, this.ctx.currentTime, 0.05);
    const map: Record<Bus, number> = { musica: this.volumes.musica, efeitos: this.volumes.efeitos, ambiente: this.volumes.ambiente, ui: this.volumes.ui };
    for (const [bus, g] of this.buses) g.gain.setTargetAtTime(map[bus], this.ctx.currentTime, 0.05);
    this.leitos?.gain.setTargetAtTime(this.volumes.ambiente, this.ctx.currentTime, 0.05);
  }

  /** Passa-baixa no bus mestre (exceto UI), suavizado. */
  setLowpass(hz: number): void {
    this.lowpassHz = hz;
    if (this.ctx && this.lowpass) this.lowpass.frequency.setTargetAtTime(hz, this.ctx.currentTime, 0.15);
  }

  /** Quanto vai para o reverb (0 a 1): noite e grutas soam mais abertas. */
  setReverb(amount: number): void {
    if (this.ctx && this.reverbSend) this.reverbSend.gain.setTargetAtTime(Math.max(0, Math.min(1, amount)) * 0.35, this.ctx.currentTime, 0.5);
  }

  /** Ouvinte na câmera: posição e para onde olha. */
  setListener(x: number, y: number, z: number, fx: number, fy: number, fz: number): void {
    this.listener = { x, y, z };
    const l = this.ctx?.listener;
    if (!l || !this.ctx) return;
    const t = this.ctx.currentTime;
    if (l.positionX) {
      l.positionX.setTargetAtTime(x, t, 0.02);
      l.positionY.setTargetAtTime(y, t, 0.02);
      l.positionZ.setTargetAtTime(z, t, 0.02);
      l.forwardX.setTargetAtTime(fx, t, 0.02);
      l.forwardY.setTargetAtTime(fy, t, 0.02);
      l.forwardZ.setTargetAtTime(fz, t, 0.02);
      l.upX.value = 0;
      l.upY.value = 1;
      l.upZ.value = 0;
    } else {
      (l as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(x, y, z);
      (l as unknown as { setOrientation(a: number, b: number, c: number, d: number, e: number, f: number): void }).setOrientation(fx, fy, fz, 0, 1, 0);
    }
  }

  private panner(x: number, y: number, z: number, hrtf: boolean): PannerNode | undefined {
    if (!this.ctx) return undefined;
    const p = this.ctx.createPanner();
    p.panningModel = hrtf ? "HRTF" : "equalpower";
    p.distanceModel = "inverse";
    p.refDistance = 3;
    p.rolloffFactor = 1.1;
    p.maxDistance = VOICES.boss;
    if (p.positionX) {
      p.positionX.value = x;
      p.positionY.value = y;
      p.positionZ.value = z;
    } else (p as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(x, y, z);
    return p;
  }

  /** Toca um efeito: a amostra quando houver, senão a síntese (conta o pedido mesmo sem contexto, para os testes). */
  play(id: SoundId, o: PlayOptions = {}): void {
    this.played[id] = (this.played[id] ?? 0) + 1;
    const sample = o.source === "sintese" ? undefined : this.bank.get(id);
    const list = sample ? sample.buffers : o.source === "amostra" ? undefined : this.buffers.get(id);
    const ctx = this.ctx;
    if (!ctx || !list?.length || this.hidden) return;
    const priority = o.priority ?? PRIORITY.comum;
    const positional = o.x !== undefined && o.z !== undefined;
    const distance = positional ? Math.hypot(o.x! - this.listener.x, (o.y ?? this.listener.y) - this.listener.y, o.z! - this.listener.z) : 0;
    if (positional && !audible(distance, priority)) return;
    const voice = this.pool.admit(priority, distance, ctx.currentTime);
    if (!voice) return;
    if (voice.steal !== undefined) this.stopVoice(voice.steal);
    try {
      const src = ctx.createBufferSource();
      src.buffer = list[o.variation !== undefined ? o.variation % list.length : this.picker.pick(id, list.length)]!;
      src.playbackRate.value = (o.rate ?? 1 + (Math.random() * 2 - 1) * JITTER[sample ? "amostra" : "sintese"]) * (sample?.rate ?? 1);
      const g = ctx.createGain();
      g.gain.value = (o.gain ?? 1) * (sample?.gain ?? 1);
      let node: AudioNode = src;
      // Passos de grama e lama (as amostras de terra mais abafadas).
      if (sample?.lowpass) {
        const f = ctx.createBiquadFilter();
        f.type = "lowpass";
        f.frequency.value = sample.lowpass;
        node = node.connect(f);
      }
      node = node.connect(g);
      if (positional) {
        const p = this.panner(o.x!, o.y ?? this.listener.y, o.z!, distance < VOICES.hrtf && priority >= PRIORITY.importante);
        if (p) node = node.connect(p);
      }
      node.connect(this.buses.get(o.bus ?? "efeitos")!);
      src.onended = () => {
        this.pool.release(voice.id);
        this.sources.delete(voice.id);
      };
      this.sources.set(voice.id, src);
      src.start(ctx.currentTime + (o.delay ?? 0));
      this.started[id] = (this.started[id] ?? 0) + 1;
      if (sample) this.samplesStarted[id] = (this.samplesStarted[id] ?? 0) + 1;
    } catch {
      this.pool.release(voice.id);
    }
  }

  private stopVoice(id: number): void {
    try {
      this.sources.get(id)?.stop();
    } catch {
      // já parou
    }
    this.sources.delete(id);
  }

  /**
   * Som em loop (ambiente, fogo, vento, redemoinho), com posição e ganho atualizáveis. Uma amostra
   * com pontos de loop (o fogo, F46) repete só o trecho entre eles; começa num ponto sorteado
   * (`seam`: 2 s antes da emenda, para ouvi-la na bancada).
   */
  loop(id: SoundId, bus: Bus = "ambiente", o: { seam?: boolean } = {}): LoopHandle {
    this.played[id] = (this.played[id] ?? 0) + 1;
    const ctx = this.ctx;
    const found = this.bank.get(id);
    const sample = found?.buffers[0] ? found : undefined;
    const buffer = sample?.buffers[0] ?? this.buffers.get(id)?.[0];
    if (!ctx || !buffer) return { live: false, set: () => undefined, stop: () => undefined };
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const loopStart = sample?.loopStart ?? 0;
    const loopEnd = sample?.loopEnd ?? buffer.duration;
    if (sample?.loopEnd !== undefined) {
      src.loopStart = loopStart;
      src.loopEnd = loopEnd;
    }
    src.playbackRate.value = sample?.rate ?? 1;
    const level = sample?.gain ?? 1;
    const g = ctx.createGain();
    g.gain.value = 0;
    let p: PannerNode | undefined;
    src.connect(g);
    let wired = false;
    const handle: LoopHandle = {
      live: true,
      set: (x, y, z, gain) => {
        if (!wired) {
          wired = true;
          if (x !== undefined) p = this.panner(x, y, z, false);
          (p ? g.connect(p) : g).connect(this.buses.get(bus)!);
          src.start(ctx.currentTime, o.seam ? Math.max(loopStart, loopEnd - 2) : loopStart + Math.random() * (loopEnd - loopStart));
          this.started[id] = (this.started[id] ?? 0) + 1;
          if (sample) this.samplesStarted[id] = (this.samplesStarted[id] ?? 0) + 1;
        }
        if (p && x !== undefined) {
          if (p.positionX) {
            p.positionX.setTargetAtTime(x, ctx.currentTime, 0.05);
            p.positionY.setTargetAtTime(y, ctx.currentTime, 0.05);
            p.positionZ.setTargetAtTime(z, ctx.currentTime, 0.05);
          } else (p as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(x, y, z);
        }
        g.gain.setTargetAtTime(gain * level, ctx.currentTime, 0.2);
      },
      stop: (fade = 0.5) => {
        this.loops.delete(handle);
        g.gain.setTargetAtTime(0, ctx.currentTime, fade / 3);
        try {
          if (wired) src.stop(ctx.currentTime + fade + 0.1);
        } catch {
          // já parou
        }
      },
    };
    this.loops.add(handle);
    return handle;
  }

  /**
   * Leitos de ambiente (F46): aplica a mistura do quadro e devolve o nível que soa de fato de cada
   * leito e o quanto de cada camada sintetizada toca por baixo.
   */
  ambience(mix: AmbienceMix): { audible: Partial<Record<BedId, number>>; cover: SynthCover } {
    return this.ambiencePlayer.apply(mix);
  }

  /** Bancada (`?debug=sons`): um leito sozinho, do começo do loop ou 2 s antes da emenda. */
  previewBed(id: BedId, seam = false): Promise<(() => void) | undefined> {
    return this.ambiencePlayer.preview(id, seam);
  }

  /** Muda o estado da trilha (crossfade); `intensity` sobe as camadas (0 a 1). */
  setMusic(state: MusicState, intensity = 0, seed = this.seed): void {
    this.intensity = intensity;
    if (state === this.music && seed === this.seed) return;
    this.seed = seed;
    this.music = state;
    this.route(crossfade(state));
  }

  /** A faixa gravada do estado (a partitura sai) ou, sem ela, a partitura. */
  private route(fade: { out: number; in: number }): void {
    if (this.player.set(trackFor(this.music), fade)) this.fadeScore(fade.out);
    else this.startState(fade);
  }

  /** A faixa do estado atual não toca: a partitura entra no lugar. */
  private onTrackFail(id: TrackId): void {
    if (trackFor(this.music) === id) this.startState(crossfade(this.music));
  }

  /** A partitura sai em `out` s e o sequenciador para. */
  private fadeScore(out: number): void {
    const ctx = this.ctx;
    const old = this.musicGain;
    this.musicGain = undefined;
    if (!ctx || !old) return;
    // Parte do valor de agora (a rampa de entrada pode estar no meio): sem isso, ela continua subindo.
    const t = ctx.currentTime;
    const v = old.gain.value;
    old.gain.cancelScheduledValues(t);
    old.gain.setValueAtTime(v, t);
    old.gain.setTargetAtTime(0, t, out / 3);
    setTimeout(() => old.disconnect(), (out + 1) * 1000);
  }

  private startState(fade: { out: number; in: number }): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.fadeScore(fade.out);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(1, ctx.currentTime + fade.in);
    g.connect(this.buses.get("musica")!);
    this.musicGain = g;
    this.bar = 0;
    this.nextBar = ctx.currentTime + 0.1;
  }

  private schedule(): void {
    const ctx = this.ctx;
    const out = this.musicGain;
    if (!ctx || !out || this.music === "silencio" || this.hidden) return;
    // Aba volta depois de muito tempo: não despeja compassos atrasados.
    if (this.nextBar < ctx.currentTime - 1) this.nextBar = ctx.currentTime + 0.05;
    const samples: Samples = { get: (id) => this.buffers.get(id)?.[0], noise: this.buffers.get("vento")?.[0] };
    while (this.nextBar < ctx.currentTime + LOOKAHEAD) {
      const state = this.music;
      const beat = 60 / tempo(state as Exclude<MusicState, "silencio">, this.bar, this.intensity);
      for (const e of scoreBar(state, this.bar, this.seed, this.intensity)) {
        playNote(ctx, out, e, this.nextBar + e.t * beat, beat, samples);
        this.notes++;
      }
      this.nextBar += 4 * beat;
      this.bar++;
    }
  }

  get ready(): number {
    return this.buffers.size;
  }

  /** Variações de um som pela fonte (a página `?debug=sons` mede e compara as duas). */
  variations(id: SoundId, source: SoundSource): readonly AudioBuffer[] {
    return (source === "amostra" ? this.bank.get(id)?.buffers : this.buffers.get(id)) ?? [];
  }

  /** O que muda numa amostra na hora de tocar (ganho, tom, passa-baixa) e os pontos de loop dela. */
  sampleShape(id: SoundId): { gain: number; rate: number; lowpass?: number; loopStart?: number; loopEnd?: number } | undefined {
    const s = this.bank.get(id);
    return s && { gain: s.gain, rate: s.rate, ...(s.lowpass !== undefined && { lowpass: s.lowpass }), ...(s.loopEnd !== undefined && { loopStart: s.loopStart ?? 0, loopEnd: s.loopEnd }) };
  }

  stats(): AudioStats {
    return {
      ready: this.buffers.size,
      state: this.ctx?.state ?? "sem-contexto",
      unlocked: !!this.ctx,
      hidden: this.hidden,
      music: this.music,
      lowpass: this.lowpassHz,
      voices: this.pool.size,
      loops: this.loops.size,
      played: { ...this.played },
      started: { ...this.started },
      notes: this.notes,
      track: this.player.stats(),
      samples: { ...this.bank.stats(), started: { ...this.samplesStarted } },
      ambience: this.ambiencePlayer.stats(),
    };
  }

  /** Para os loops (fim da cena). */
  stopLoops(): void {
    for (const l of [...this.loops]) l.stop(0.3);
  }

  /** Fim da cena: para loops e efeitos pendentes (até os agendados, como a explosão da Pedra Fulminada). */
  stopScene(): void {
    this.stopLoops();
    this.ambiencePlayer.stop();
    for (const id of [...this.sources.keys()]) {
      this.stopVoice(id);
      this.pool.release(id);
    }
  }

  /** Mixagem neutra fora da partida: sem o passa-baixa de Vida, água ou espectador, reverb baixo. */
  resetMix(): void {
    this.setLowpass(22_000);
    this.setReverb(0.1);
  }

  dispose(): void {
    this.stopLoops();
    if (this.timer) clearInterval(this.timer);
    this.player.dispose();
    void this.ctx?.close().catch(() => undefined);
    this.ctx = undefined;
  }
}

/** O áudio do jogo (um por página). */
export const audio = new AudioEngine();
